import { useEffect, useRef } from "react";
import CustomButton from "./CustomButton";

export type Section = {
  id: number;
  name: string;
};

type StepsBarProps = {
  current: number;
  sections: Section[];
  onStepClick?: (step: number) => void;
};

export default function StepsBar({
  current,
  sections,
  onStepClick,
}: StepsBarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [current]);

  return (
    <div
      ref={containerRef}
      className="flex gap-6 w-full overflow-x-auto no-scrollbar py-2"
    >
      {sections.map((section, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === current;

        return (
          <div key={section.id} className="flex items-center gap-6 shrink-0">
            {/* Step */}
            <button
              ref={isActive ? activeRef : null}
              type="button"
              onClick={() => onStepClick?.(stepNumber)}
              className="flex items-center gap-2 shrink-0 focus:outline-none"
            >
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                  ${
                    isActive
                      ? "bg-primary-600 text-white"
                      : "bg-white text-neutral-400 "
                  }
                `}
              >
                {stepNumber}
              </div>

              <span
                className={`text-[15px] font-medium whitespace-nowrap
                  ${isActive ? "text-primary-600" : "text-neutral-400"}
                `}
              >
                {section.name}
              </span>
            </button>

            {/* Divider */}
            {index !== sections.length - 1 && (
              <div
                className={`w-12 h-0.5 transition-colors
                  ${isActive ? "bg-primary-600" : "bg-neutral-200"}
                `}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface StepperActionsProps {
  isFirst: boolean;
  isLast: boolean;
  isNextDisabled?: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit?: () => void;
}

export function StepperActions({
  isFirst,
  isLast,
  isNextDisabled,
  onBack,
  onNext,
  onSubmit,
}: StepperActionsProps) {
  return (
    <div className="flex justify-between pt-4">
      {!isFirst ? (
        <CustomButton color="bg-neutral-200" text="Back" onClick={onBack} />
      ) : (
        <div />
      )}

      {isLast ? (
        <CustomButton
          text="Submit"
          color="bg-success-600"
          onClick={onSubmit}
          disabled={isNextDisabled}
        />
      ) : (
        <CustomButton
          text="Next"
          color="bg-primary-600"
          onClick={onNext}
          disabled={isNextDisabled}
        />
      )}
    </div>
  );
}
